import type { ClassValue } from 'clsx'
import { clsx } from 'clsx'
import { extendTailwindMerge } from 'tailwind-merge'

/**
 * `twMerge` taught the kit's own utility families (2026-10-03).
 *
 * `cn(variantClasses, props.class)` is how every kit component lets the app
 * override it, and that only works when tailwind-merge recognises the two
 * classes as the same CSS property and drops the earlier one. It does not know
 * the kit's names, and for shadows it misfiles them: `shadow-modern-sm` reads as
 * a shadow COLOUR (the colour scale accepts any word), `shadow-lg` as a shadow
 * SIZE, so `<Button class="shadow-lg">` kept both — and because the kit's rules
 * are emitted after the preset's, `shadow-modern-sm` won and the app's class
 * silently did nothing. Each family below is registered with the group whose
 * property it actually sets, so the class that comes LAST (the consumer's) wins.
 *
 * Only single-property families are listed. Multi-property shortcuts (`ui-md` =
 * height + padding + font-size + gap) cannot be expressed as one group: dropping
 * the whole shortcut because the app overrode its height would lose the rest.
 */
const KIT_SHADOW = /^(?:modern|theme|inset)-[\w-]+$/
const KIT_DROP_SHADOW = /^modern-(?:sm|md|lg)$/
/** Values the kit's logical-property rules accept (`pi-3`, `inset-inline-start-1/2`, `-[x]`). */
const LOGICAL_VALUE = (v: string): boolean => /^(?:auto|px|full|\d+(?:\.\d+)?|\d+\/\d+|\[.+\])$/.test(v)

const kitMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'shadow': [{ shadow: [(v: string) => KIT_SHADOW.test(v)] }],
      'drop-shadow': [{ 'drop-shadow': [(v: string) => KIT_DROP_SHADOW.test(v)] }],
      // padding-inline / -start / -end
      'px': [{ pi: [LOGICAL_VALUE] }],
      'ps': [{ pis: [LOGICAL_VALUE] }],
      'pe': [{ pie: [LOGICAL_VALUE] }],
      // logical insets
      'start': [{ 'inset-inline-start': [LOGICAL_VALUE] }],
      'end': [{ 'inset-inline-end': [LOGICAL_VALUE] }],
      'top': [{ 'inset-block-start': [LOGICAL_VALUE] }],
      'bottom': [{ 'inset-block-end': [LOGICAL_VALUE] }],
    },
  },
})

export function cn(...inputs: ClassValue[]): string {
  return kitMerge(clsx(inputs))
}
